import { FormControl, FormField } from '@/components/ui/Form';
import { Form, FormInput, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function UserForm ({ data, onChangeField, onCompleted }) {
    // Move schema definition here so i18next.t is called after translations are loaded
    const formSchema = z.object({
        email: z.string().email(i18next.t('form_validations.users.email_invalid')),
        firstName: z.string().min(1, i18next.t('form_validations.users.first_name_required')),
        lastName: z.string().min(1, i18next.t('form_validations.users.last_name_required')),
        telephone: z
            .string()
            .regex(/^\+[0-9]{1,4}( ?[0-9]){8,14}$/, i18next.t('form_validations.users.telephone_invalid'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: data.email || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            telephone: data.telephone || ''
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = React.useRef({
        email: debounce(() => form.trigger('email'), 600),
        firstName: debounce(() => form.trigger('firstName'), 600),
        lastName: debounce(() => form.trigger('lastName'), 600),
        telephone: debounce(() => form.trigger('telephone'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <Form {...form}>
            <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('users.first_name')}</FormLabel>
                        <FormControl>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    field.onChange(e);
                                    onChangeField('firstName')(e);
                                    debouncedTriggers.current.firstName();
                                }}
                                placeholder={i18next.t('users.first_name')}
                            />
                        </FormControl>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('users.last_name')}</FormLabel>
                        <FormControl>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    field.onChange(e);
                                    onChangeField('lastName')(e);
                                    debouncedTriggers.current.lastName();
                                }}
                                placeholder={i18next.t('users.last_name')}
                            />
                        </FormControl>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('common.email')}</FormLabel>
                        <FormControl>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    field.onChange(e);
                                    onChangeField('email')(e);
                                    debouncedTriggers.current.email();
                                }}
                                placeholder="m@example.com"
                                type="email"
                            />
                        </FormControl>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="telephone"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('users.telephone_number')}</FormLabel>
                        <FormControl>
                            <FormInput
                                {...field}
                                onChange={e => {
                                    field.onChange(e);
                                    onChangeField('telephone')(e);
                                    debouncedTriggers.current.telephone();
                                }}
                                placeholder="+351 999999999"
                                type="tel"
                            />
                        </FormControl>
                    </FormItem>
                )}
            />
        </Form>
    );
}
